// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { log } from '../log/log';
import type {
  Location,
  LocationSearchResult,
  LocationShareKind,
  LocationTab,
  ShareLocationCallbacks,
  ShareLocationResult,
} from './types';

export interface ShareLocationViewModelState {
  readonly query: string;
  readonly results: readonly LocationSearchResult[];
  readonly searching: boolean;
  readonly searchError: 'none' | 'failed';
  readonly selected: LocationSearchResult | null;
  readonly currentLocation: Location | null;
  readonly resolvingCurrent: boolean;
  readonly currentLocationError: 'none' | 'denied' | 'failed';
  readonly liveEnabled: boolean;
  readonly liveDurationSeconds: number;
  /** Active sidebar tab — "static" pin search vs the device "current" position. */
  readonly tab: LocationTab;
}

export interface ShareLocationViewModel extends ShareLocationViewModelState {
  readonly setQuery: (next: string) => void;
  readonly selectResult: (next: LocationSearchResult | null) => void;
  readonly useCurrentLocation: () => Promise<void>;
  readonly setLiveEnabled: (next: boolean) => void;
  readonly setLiveDurationSeconds: (next: number) => void;
  /** Switch sidebar tabs. Switching to "current" kicks off geolocation. */
  readonly setTab: (next: LocationTab) => void;
  /**
   * Produces a {@link ShareLocationResult} if a target is selected
   * (either an explicit search result, the resolved current location,
   * or both — the explicit result wins). Returns `null` otherwise.
   *
   * The returned object holds the GPS coordinates by reference. The
   * caller is responsible for handing it to {@link ShareLocationCallbacks.onShare}
   * and discarding it after. The viewmodel itself never logs the
   * coordinates and they remain in memory only.
   */
  readonly buildShareResult: () => ShareLocationResult | null;
}

const DEFAULT_LIVE_DURATION = 60 * 60;
const SEARCH_DEBOUNCE_MS = 220;

export function useShareLocationViewModel(
  callbacks: Pick<ShareLocationCallbacks, 'onSearch' | 'onResolveCurrentLocation'>,
  defaultLiveDurationSeconds: number = DEFAULT_LIVE_DURATION,
): ShareLocationViewModel {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly LocationSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<'none' | 'failed'>('none');
  const [selected, setSelected] = useState<LocationSearchResult | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [resolvingCurrent, setResolvingCurrent] = useState(false);
  const [currentLocationError, setCurrentLocationError] = useState<
    'none' | 'denied' | 'failed'
  >('none');
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [liveDurationSeconds, setLiveDurationSeconds] = useState(
    defaultLiveDurationSeconds,
  );
  const [tab, setTab] = useState<LocationTab>('static');

  const searchController = useRef<AbortController | null>(null);
  const currentController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!callbacks.onSearch) {
      return;
    }
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      setSearchError('none');
      searchController.current?.abort();
      return;
    }

    const controller = new AbortController();
    searchController.current?.abort();
    searchController.current = controller;
    setSearching(true);
    setSearchError('none');

    const timer = setTimeout(() => {
      callbacks.onSearch?.(trimmed, controller.signal)
        .then((next) => {
          if (controller.signal.aborted) {
            return;
          }
          setResults(next);
          setSearching(false);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          const message =
            error instanceof Error ? error.name : 'unknown';
          // We log only the failure category — never the query (PII)
          // nor any provider payload.
          log.warn('share_location.search.failed', { errorName: message });
          setSearching(false);
          setSearchError('failed');
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [callbacks, query]);

  useEffect(
    () => () => {
      searchController.current?.abort();
      currentController.current?.abort();
    },
    [],
  );

  const selectResult = useCallback((next: LocationSearchResult | null) => {
    setSelected(next);
  }, []);

  const useCurrentLocation = useCallback(async () => {
    if (!callbacks.onResolveCurrentLocation) {
      return;
    }
    const controller = new AbortController();
    currentController.current?.abort();
    currentController.current = controller;
    setResolvingCurrent(true);
    setCurrentLocationError('none');
    try {
      const location = await callbacks.onResolveCurrentLocation(controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      if (location) {
        setCurrentLocation(location);
        setSelected(null);
      } else {
        setCurrentLocationError('denied');
      }
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        return;
      }
      const name = error instanceof Error ? error.name : 'unknown';
      // GPS coordinates are NEVER logged. The error name only.
      log.warn('share_location.current.failed', { errorName: name });
      setCurrentLocationError('failed');
    } finally {
      if (!controller.signal.aborted) {
        setResolvingCurrent(false);
      }
    }
  }, [callbacks]);

  const handleSetTab = useCallback(
    (next: LocationTab) => {
      setTab(next);
      if (next === 'current') {
        // Switching to the "Current location" tab resolves the device GPS
        // through the host-supplied resolver (web: navigator.geolocation).
        void useCurrentLocation();
      }
    },
    [useCurrentLocation],
  );

  const buildShareResult = useCallback((): ShareLocationResult | null => {
    const target: Location | null = selected ? selected.location : currentLocation;
    if (!target) {
      return null;
    }
    const kind: LocationShareKind = liveEnabled ? 'live' : 'point';
    if (kind === 'live') {
      return {
        location: target,
        kind,
        liveDurationSeconds,
      };
    }
    return { location: target, kind };
  }, [currentLocation, liveDurationSeconds, liveEnabled, selected]);

  return useMemo<ShareLocationViewModel>(
    () => ({
      query,
      results,
      searching,
      searchError,
      selected,
      currentLocation,
      resolvingCurrent,
      currentLocationError,
      liveEnabled,
      liveDurationSeconds,
      tab,
      setQuery,
      selectResult,
      useCurrentLocation,
      setLiveEnabled,
      setLiveDurationSeconds,
      setTab: handleSetTab,
      buildShareResult,
    }),
    [
      buildShareResult,
      currentLocation,
      currentLocationError,
      handleSetTab,
      liveDurationSeconds,
      liveEnabled,
      query,
      resolvingCurrent,
      results,
      searchError,
      searching,
      selectResult,
      selected,
      tab,
      useCurrentLocation,
    ],
  );
}
